import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, MapPin, ShieldCheck, Truck } from "lucide-react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import { LANDING_PAGES } from "../data/landingPages";

const NEIGHBORHOOD_PAGES = LANDING_PAGES.filter((p) => p.slug.startsWith("weed-delivery-") && p.slug !== "weed-delivery-dc");

export default function ServiceAreas() {
  return (
    <main data-testid="service-areas-page">
      <Seo
        title="DC Weed Delivery Service Areas | Farmacy District DC"
        description="Farmacy District DC delivers premium cannabis to every major Washington, DC neighborhood — Wharf, Shaw, NoMa, Navy Yard, Georgetown, Adams Morgan and more. 30–90 minutes, 21+."
      />
      <section className="relative bg-brand-ink py-20 text-white grain sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-lime">Neighborhoods We Serve</p>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-black leading-[0.95] tracking-tighter sm:text-6xl">
              Weed Delivery Service Areas in Washington, DC
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-300">
              We deliver to all major DC neighborhoods, residential areas, hotels and short-term stays — in compliance
              with local cannabis laws. We deliver within Washington, DC only.
            </p>
            <Link
              to="/shop"
              data-testid="service-areas-order-cta"
              className="mt-8 inline-block rounded-full bg-brand-lime px-9 py-4 text-sm font-bold tracking-wide text-brand-ink transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-brand-limehover"
            >
              ORDER NOW
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
        <Reveal>
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Your Trusted DC Cannabis Delivery Locations
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-4 lg:grid-cols-2 lg:gap-6">
          {NEIGHBORHOOD_PAGES.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 2) * 0.05}>
              <div
                className="group h-full border border-neutral-200 bg-white p-7 transition-[border-color,box-shadow] duration-300 hover:border-brand-ink hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
                data-testid={`service-area-${p.slug}`}
              >
                <Link to={`/${p.slug}`} className="flex items-center justify-between">
                  <span className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 flex-shrink-0 text-brand-red" />
                    <span className="font-display text-lg font-bold tracking-tight">
                      {p.h1.replace("Weed Delivery in ", "")}
                    </span>
                  </span>
                  <ArrowRight className="h-4 w-4 text-neutral-400 transition-[transform,color] duration-200 group-hover:translate-x-1 group-hover:text-brand-ink" />
                </Link>
                <p className="mt-3 text-sm leading-relaxed text-neutral-600">{p.intro}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-8 text-sm text-neutral-500">
            Don't see your neighborhood? If you're in the District, we deliver. Call{" "}
            <a href="tel:+12029134523" className="font-bold text-brand-ink">(202) 913-4523</a> to confirm.
          </p>
        </Reveal>
      </section>

      <section className="bg-brand-sand py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
              Shop Farmacy District's Premium Cannabis Selection and Daily Deals
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-neutral-600">
              Discover Farmacy District, your trusted source for high-quality cannabis in Washington, D.C. Explore our
              wide range of products, including:
            </p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {[
                { text: "Fresh, hand-selected cannabis flower", to: "/category/flower-strains" },
                { text: "Delicious and potent infused edibles", to: "/category/edibles" },
                { text: "High-quality concentrates for experienced users", to: "/category/concentrates" },
                { text: "Convenient vape cartridges for on-the-go enjoyment", to: "/category/cartridges" },
                { text: "Pre-rolls and curated bundles for daily deals", to: "/category/special-deals" },
              ].map((l) => (
                <li key={l.text}>
                  <Link
                    to={l.to}
                    className="flex items-center gap-2.5 text-sm font-semibold text-neutral-700 transition-colors hover:text-brand-red"
                    data-testid={`service-areas-shop-${l.to.split("/").pop()}`}
                  >
                    <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand-lime" />
                    {l.text}
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/shop"
              className="mt-8 inline-block rounded-full bg-brand-ink px-8 py-3.5 text-sm font-bold tracking-wide text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-neutral-800"
              data-testid="service-areas-shop-cta"
            >
              SHOP THE MENU
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-sand py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 sm:grid-cols-3 sm:px-6 lg:gap-6">
          {[
            { icon: Truck, title: "30–90 Minutes", text: "Fast delivery windows with clear communication at every step." },
            { icon: ShieldCheck, title: "Always Discreet", text: "Unmarked, professional delivery to homes, hotels and short-term stays." },
            { icon: BadgeCheck, title: "21+ & I-71 Compliant", text: "One-time age verification at checkout, valid ID on delivery." },
          ].map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="h-full border border-neutral-200 bg-white p-8">
                <f.icon className="h-6 w-6 text-brand-red" />
                <h2 className="mt-3 font-display text-lg font-bold tracking-tight">{f.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-neutral-500">{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
